import React, { useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Paper,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Alert,
  AlertTitle,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Phone,
  LocationOn,
  Badge,
  Email,
  CalendarMonth,
  Shield,
  Person,
  SwapHoriz,
  Error,
  CheckCircle,
  Warning
} from '@mui/icons-material';
import { migrateMemberToGroup } from './api';

const PersonalDetails = ({ user, groups, onMigrationSuccess }) => {
  const [showMigrationDialog, setShowMigrationDialog] = useState(false);
  const [migrationForm, setMigrationForm] = useState({
    new_group_id: '',
    reason: '',
    notes: '',
    officer_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Format date to local string
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString();
  };

  // Get status display properties based on is_active
  const getStatusProps = (isActive) => {
    if (isActive) {
      return {
        label: 'Active',
        chipBg: '#dcfce7',
        chipColor: '#166534',
        chipBorder: '#bbf7d0',
        avatarBg: '#16a34a',
        iconColor: 'white'
      };
    } else {
      return {
        label: 'Inactive',
        chipBg: '#fee2e2',
        chipColor: '#991b1b',
        chipBorder: '#fecaca',
        avatarBg: '#dc2626',
        iconColor: 'white'
      };
    }
  };

  const statusProps = getStatusProps(user.is_active);

  // Handle migration submission
  const handleMigrationSubmit = async () => {
    // Validation
    if (!migrationForm.new_group_id) {
      setError({ message: 'Please select a new group' });
      return;
    }
    if (!migrationForm.reason.trim()) {
      setError({ message: 'Please provide a reason for migration' });
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await migrateMemberToGroup({
        member_id: user.id,
        old_group_id: user.group_id || user.group,
        new_group_id: parseInt(migrationForm.new_group_id),
        reason: migrationForm.reason,
        notes: migrationForm.notes,
        officer_name: migrationForm.officer_name || 'System'
      });

      setSuccess(response);
      
      // Reset form
      setMigrationForm({
        new_group_id: '',
        reason: '',
        notes: '',
        officer_name: ''
      });

      // Close dialog after 2 seconds
      setTimeout(() => {
        setShowMigrationDialog(false);
        if (onMigrationSuccess) {
          onMigrationSuccess(response);
        }
      }, 2000);

    } catch (err) {
      console.error('Migration error:', err);
      
      // Handle different error formats
      if (err.response?.data) {
        setError({
          message: err.response.data.message || err.response.data.error || 'Migration failed',
          details: err.response.data
        });
      } else {
        setError({
          message: err.message || 'An unexpected error occurred'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseMigrationDialog = () => {
    setShowMigrationDialog(false);
    setError(null);
    setSuccess(null);
    setMigrationForm({
      new_group_id: '',
      reason: '',
      notes: '',
      officer_name: ''
    });
  };

  // Get available groups (exclude current group)
  const availableGroups = groups.filter(g => g.id !== user.group_id);

  return (
    <Box sx={{ maxWidth: '4xl', mx: 'auto' }}>
      <Card sx={{ 
        overflow: 'hidden', 
        boxShadow: 3, 
        border: 0, 
        backgroundColor: 'background.paper'
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
            {/* Personal Details Section */}
            <Box>
              <Typography variant="h6" sx={{ 
                fontWeight: 'semibold', 
                color: 'primary.main', 
                mb: 3, 
                display: 'flex', 
                alignItems: 'center' 
              }}>
                Member Info
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Paper sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5, 
                  p: 1.5, 
                  backgroundColor: 'background.default',
                  '&:hover': { backgroundColor: 'action.hover' },
                  transition: 'background-color 0.2s',
                  border: 1,
                  borderColor: 'divider'
                }}>
                  <Avatar sx={{ 
                    width: 40, 
                    height: 40, 
                    backgroundColor: '#3b82f6' 
                  }}>
                    <Phone sx={{ width: 20, height: 20, color: 'white' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'medium' }}>
                      Phone
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'semibold', color: 'text.primary' }}>
                      {user.phone}
                    </Typography>
                  </Box>
                </Paper>

                <Paper sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5, 
                  p: 1.5, 
                  backgroundColor: 'background.default',
                  '&:hover': { backgroundColor: 'action.hover' },
                  transition: 'background-color 0.2s',
                  border: 1,
                  borderColor: 'divider'
                }}>
                  <Avatar sx={{ 
                    width: 40, 
                    height: 40, 
                    backgroundColor: '#22c55e' 
                  }}>
                    <LocationOn sx={{ width: 20, height: 20, color: 'white' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'medium' }}>
                      Location
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'semibold', color: 'text.primary' }}>
                      {user.location}
                    </Typography>
                  </Box>
                </Paper>

                <Paper sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5, 
                  p: 1.5, 
                  backgroundColor: 'background.default',
                  '&:hover': { backgroundColor: 'action.hover' },
                  transition: 'background-color 0.2s',
                  border: 1,
                  borderColor: 'divider'
                }}>
                  <Avatar sx={{ 
                    width: 40, 
                    height: 40, 
                    backgroundColor: '#a855f7' 
                  }}>
                    <Badge sx={{ width: 20, height: 20, color: 'white' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'medium' }}>
                      ID Number
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'semibold', color: 'text.primary' }}>
                      {user.id_number}
                    </Typography>
                  </Box>
                </Paper>

                {user.occupation && (
                  <Paper sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5, 
                    p: 1.5, 
                    backgroundColor: 'background.default',
                    '&:hover': { backgroundColor: 'action.hover' },
                    transition: 'background-color 0.2s',
                    border: 1,
                    borderColor: 'divider'
                  }}>
                    <Avatar sx={{ 
                      width: 40, 
                      height: 40, 
                      backgroundColor: '#f59e0b' 
                    }}>
                      <Person sx={{ width: 20, height: 20, color: 'white' }} />
                    </Avatar>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'medium' }}>
                        Occupation
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'semibold', color: 'text.primary' }}>
                        {user.occupation}
                      </Typography>
                    </Box>
                  </Paper>
                )}
              </Box>
            </Box>

            {/* Membership Status Section */}
            <Box>
              <Typography variant="h6" sx={{ 
                fontWeight: 'semibold', 
                color: user.is_active ? 'success.main' : 'error.main', 
                mb: 3, 
                display: 'flex', 
                alignItems: 'center' 
              }}>
                <Shield sx={{ 
                  width: 20, 
                  height: 20, 
                  mr: 1, 
                  color: user.is_active ? 'success.main' : 'error.main' 
                }} />
                Membership Status
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ 
                    width: 64, 
                    height: 64, 
                    backgroundColor: statusProps.avatarBg
                  }}>
                    <Shield sx={{ width: 32, height: 32, color: statusProps.iconColor }} />
                  </Avatar>
                  <Box>
                    <Chip 
                      label={statusProps.label}
                      sx={{ 
                        mb: 1, 
                        px: 2, 
                        py: 1, 
                        fontWeight: 'medium',
                        backgroundColor: statusProps.chipBg,
                        color: statusProps.chipColor,
                        border: `1px solid ${statusProps.chipBorder}`
                      }}
                    />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Current Status
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Paper sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    p: 1.5, 
                    backgroundColor: 'background.default',
                    border: 1,
                    borderColor: 'divider'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarMonth sx={{ width: 16, height: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                        Member Since
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Chip 
                        label={user.membership_duration}
                        size="small"
                        sx={{ 
                          mb: 0.5,
                          backgroundColor: '#dbeafe',
                          color: '#1e40af',
                          border: '1px solid #bfdbfe'
                        }}
                      />
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        {formatDate(user.registration_date)}
                      </Typography>
                    </Box>
                  </Paper>

                  {/* Migration Button - Only show for active members */}
                  {user.is_active && (
                    <Button
                      variant="contained"
                      startIcon={<SwapHoriz />}
                      onClick={() => setShowMigrationDialog(true)}
                      fullWidth
                      sx={{
                        mt: 1,
                        background: 'linear-gradient(90deg, #f97316 0%, #ef4444 100%)',
                        '&:hover': {
                          background: 'linear-gradient(90deg, #ea580c 0%, #dc2626 100%)',
                        },
                        py: 1.5,
                        fontWeight: 'bold',
                        textTransform: 'none'
                      }}
                    >
                      Migrate to Different Group
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Migration Dialog */}
      <Dialog
          open={showMigrationDialog}
          onClose={handleCloseMigrationDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: 'background.paper',
              backgroundImage: 'none'
            }
          }}
        >
        <DialogTitle sx={{
          background: 'linear-gradient(90deg, #f97316 0%, #ef4444 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <SwapHoriz />
          Migrate Member to Different Group
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {/* Success Message */}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <AlertTitle>Migration Successful!</AlertTitle>
              {success.message}
              {success.migration_details && (
                <Box sx={{ mt: 1, fontSize: '0.875rem' }}>
                  <Typography variant="caption" display="block">
                    From: <strong>{success.migration_details.old_group.name}</strong>
                  </Typography>
                  <Typography variant="caption" display="block">
                    To: <strong>{success.migration_details.new_group.name}</strong>
                  </Typography>
                </Box>
              )}
            </Alert>
          )}

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>Migration Failed</AlertTitle>
              {error.message}
              
              {/* Show additional error details */}
              {error.details && (
                <Box sx={{ mt: 2 }}>
                  {error.details.pending_loans && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: 'error.lighter', borderRadius: 1 }}>
                      <Typography variant="caption" fontWeight="bold" display="block">
                        Pending Loans:
                      </Typography>
                      <List dense>
                        {error.details.pending_loans.map((loan, idx) => (
                          <ListItem key={idx} sx={{ py: 0 }}>
                            <ListItemText 
                              primary={loan}
                              primaryTypographyProps={{ variant: 'caption' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                  
                  {error.details.active_period && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: 'error.lighter', borderRadius: 1 }}>
                      <Typography variant="caption" fontWeight="bold" display="block">
                        Active Performance Period:
                      </Typography>
                      <Typography variant="caption" display="block">
                        {error.details.active_period.period_name} 
                        ({error.details.active_period.period_start} to {error.details.active_period.period_end})
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Alert>
          )}

          {/* Current Member Info */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.lighter' }}>
            <Typography variant="subtitle2" fontWeight="bold" color="primary.main" gutterBottom>
              Current Member Information
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Name:</Typography>
                <Typography variant="body2" fontWeight="semibold">{user.name}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Member ID:</Typography>
                <Typography variant="body2" fontWeight="semibold">{user.member_id}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Current Group:</Typography>
                <Typography variant="body2" fontWeight="semibold">{user.group_name || user.group}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Savings Balance:</Typography>
                <Typography variant="body2" fontWeight="semibold">
                  KSH {parseFloat(user.savings_balance || 0).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Migration Form */}
          {!success && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* New Group Selection */}
              <TextField
                select
                fullWidth
                label="New Group"
                required
                value={migrationForm.new_group_id}
                onChange={(e) => setMigrationForm({...migrationForm, new_group_id: e.target.value})}
                disabled={loading}
              >
                <MenuItem value="">
                  <em>Select new group...</em>
                </MenuItem>
                {availableGroups.map(group => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name} - {group.location}
                  </MenuItem>
                ))}
              </TextField>

              {/* Reason */}
              <TextField
                fullWidth
                required
                multiline
                rows={3}
                label="Reason for Migration"
                placeholder="E.g., Member relocated to new area"
                value={migrationForm.reason}
                onChange={(e) => setMigrationForm({...migrationForm, reason: e.target.value})}
                disabled={loading}
              />

              {/* Additional Notes */}
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Additional Notes (Optional)"
                placeholder="Any additional information..."
                value={migrationForm.notes}
                onChange={(e) => setMigrationForm({...migrationForm, notes: e.target.value})}
                disabled={loading}
              />

              {/* Officer Name */}
              <TextField
                fullWidth
                label="Officer Name (Optional)"
                placeholder="Your name"
                value={migrationForm.officer_name}
                onChange={(e) => setMigrationForm({...migrationForm, officer_name: e.target.value})}
                disabled={loading}
              />

              {/* Warning Message */}
              <Alert severity="warning" icon={<Warning />}>
                <AlertTitle>Important</AlertTitle>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  <Typography component="li" variant="caption">
                    Member's financial balances will be preserved
                  </Typography>
                  <Typography component="li" variant="caption">
                    Past records will remain with the old group
                  </Typography>
                  <Typography component="li" variant="caption">
                    Future transactions will be in the new group
                  </Typography>
                  <Typography component="li" variant="caption">
                    Migration cannot be undone automatically
                  </Typography>
                </Box>
              </Alert>
            </Box>
          )}
        </DialogContent>

        {/* Dialog Actions */}
        {!success && (
          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button
              onClick={handleCloseMigrationDialog}
              disabled={loading}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleMigrationSubmit}
              disabled={loading || !migrationForm.new_group_id || !migrationForm.reason.trim()}
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <SwapHoriz />}
              sx={{
                background: 'linear-gradient(90deg, #f97316 0%, #ef4444 100%)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #ea580c 0%, #dc2626 100%)',
                }
              }}
            >
              {loading ? 'Migrating...' : 'Confirm Migration'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
};

export default PersonalDetails;